import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { tAdmin } from "@/lib/i18n";
import { Users, Calendar, MessageCircle, TrendingUp, Settings, DollarSign, Video } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { useNavigate } from "react-router-dom";
import { logBulkProfileView } from "@/lib/audit-logger";
import { AppFooter } from "@/components/AppFooter";

interface Stats {
  totalSubscribers: number;
  activeSubscribers: number;
  todaySessions: number;
  openChats: number;
}

interface ActivityItem {
  id: string;
  type: 'message' | 'subscription' | 'session';
  timestamp: string;
  description: string;
  user?: string;
  icon: 'MessageCircle' | 'Users' | 'Calendar';
  color: string;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalSubscribers: 0,
    activeSubscribers: 0,
    todaySessions: 0,
    openChats: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminAuth();
    fetchStats();
    fetchRecentActivity();
  }, []);

  const checkAdminAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin");

    if (!roles || roles.length === 0) {
      navigate("/today");
    }
  };

  const fetchStats = async () => {
    try {
      // Total subscribers - fetch IDs to log access
      const { data: subsData, error: subsError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "subscriber");

      if (subsError) {
        console.error("Error fetching total subscribers:", subsError);
      }

      // Fetch profiles for audit logging
      if (subsData && subsData.length > 0) {
        const { data: profilesData } = await supabase
          .from("profiles")
          .select("id")
          .in("auth_user_id", subsData.map((r) => r.user_id));
        
        if (profilesData && profilesData.length > 0) {
          logBulkProfileView(profilesData.map(p => p.id));
        }
      }

      // Active subscribers
      const { count: activeSubs, error: activeError } = await supabase
        .from("subscriptions")
        .select("*", { count: "exact", head: true })
        .eq("status", "active");

      if (activeError) {
        console.error("Error fetching active subscribers:", activeError);
      }

      // Today's sessions
      const today = new Date().toISOString().slice(0, 10);
      const { count: todaySessions, error: sessionsError } = await supabase
        .from("sessions")
        .select("*", { count: "exact", head: true })
        .eq("date", today)
        .eq("status", "published");

      if (sessionsError) {
        console.error("Error fetching today's sessions:", sessionsError);
      }

      // Open chats (messages without admin reply)
      const { data: messages, error: messagesError } = await supabase
        .from("messages")
        .select("user_id")
        .eq("direction", "in");

      if (messagesError) {
        console.error("Error fetching messages:", messagesError);
      }

      const uniqueChats = new Set(messages?.map((m) => m.user_id) || []).size;

      console.log("Dashboard stats:", {
        totalSubscribers: subsData?.length || 0,
        activeSubscribers: activeSubs || 0,
        todaySessions: todaySessions || 0,
        openChats: uniqueChats,
      });

      setStats({
        totalSubscribers: subsData?.length || 0,
        activeSubscribers: activeSubs || 0,
        todaySessions: todaySessions || 0,
        openChats: uniqueChats,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      setLoadingActivities(true);
      const activities: ActivityItem[] = [];

      // Fetch recent messages (last 5)
      const { data: messages } = await supabase
        .from("messages")
        .select(`
          id,
          created_at,
          direction,
          body,
          profiles!inner(name)
        `)
        .order("created_at", { ascending: false })
        .limit(5);

      if (messages) {
        messages.forEach((msg: any) => {
          activities.push({
            id: msg.id,
            type: 'message',
            timestamp: msg.created_at,
            description: msg.direction === 'in' 
              ? `New message from ${msg.profiles?.name || 'User'}`
              : `Reply sent to ${msg.profiles?.name || 'User'}`,
            user: msg.profiles?.name,
            icon: 'MessageCircle',
            color: 'text-primary',
          });
        });
      }

      // Fetch recent subscriptions (last 3)
      const { data: subscriptions } = await supabase
        .from("subscriptions")
        .select(`
          id,
          created_at,
          status,
          plan_code,
          profiles!inner(name)
        `)
        .order("created_at", { ascending: false })
        .limit(3);

      if (subscriptions) {
        subscriptions.forEach((sub: any) => {
          activities.push({
            id: sub.id,
            type: 'subscription',
            timestamp: sub.created_at,
            description: `New subscription: ${sub.profiles?.name || 'User'} - ${sub.plan_code}`,
            user: sub.profiles?.name,
            icon: 'Users',
            color: 'text-secondary',
          });
        });
      }

      // Fetch recent sessions (last 3)
      const { data: sessions } = await supabase
        .from("sessions")
        .select("id, created_at, title_en, title_gu, status")
        .order("created_at", { ascending: false })
        .limit(3);

      if (sessions) {
        sessions.forEach((session: any) => {
          activities.push({
            id: session.id,
            type: 'session',
            timestamp: session.created_at,
            description: `Session ${session.status}: ${session.title_en || session.title_gu}`,
            icon: 'Calendar',
            color: 'text-accent-foreground',
          });
        });
      }

      // Sort all activities by timestamp (most recent first)
      activities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Keep only the 8 most recent activities
      setActivities(activities.slice(0, 8));
    } catch (error) {
      console.error("Error fetching recent activity:", error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const statCards = [
    {
      title: tAdmin.admin.stats.totalSubscribers,
      value: stats.totalSubscribers,
      icon: Users,
      color: "bg-primary/10 text-primary",
    },
    {
      title: tAdmin.admin.stats.activeSubscribers,
      value: stats.activeSubscribers,
      icon: TrendingUp,
      color: "bg-secondary/20 text-secondary",
    },
    {
      title: tAdmin.admin.stats.todaySessions,
      value: stats.todaySessions,
      icon: Calendar,
      color: "bg-accent/20 text-accent-foreground",
    },
    {
      title: tAdmin.admin.stats.openChats,
      value: stats.openChats,
      icon: MessageCircle,
      color: "bg-destructive/10 text-destructive",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{tAdmin.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))]">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="દમયંતી બાગ" className="h-10" />
              <h1 className="text-xl font-bold text-primary">{tAdmin.admin.dashboard}</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate("/profile")}>
              <Settings className="mr-2 w-4 h-4" />
              Settings
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, idx) => (
            <MbCard key={idx} elevated className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </MbCard>
          ))}
        </div>

        {/* Quick Actions */}
        <MbCard elevated className="p-6">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              variant="admin"
              className="w-full justify-start h-auto py-4"
              onClick={() => navigate("/admin/sessions")}
            >
              <Calendar className="mr-3 w-5 h-5" />
              <div className="text-left">
                <div className="font-bold">{tAdmin.admin.sessions}</div>
                <div className="text-xs opacity-80">Manage video sessions</div>
              </div>
            </Button>

            <Button
              variant="admin"
              className="w-full justify-start h-auto py-4"
              onClick={() => navigate("/admin/promo-videos")}
            >
              <Video className="mr-3 w-5 h-5" />
              <div className="text-left">
                <div className="font-bold">Promo Videos</div>
                <div className="text-xs opacity-80">Manage promotional content</div>
              </div>
            </Button>

            <Button
              variant="admin"
              className="w-full justify-start h-auto py-4"
              onClick={() => navigate("/admin/subscribers")}
            >
              <Users className="mr-3 w-5 h-5" />
              <div className="text-left">
                <div className="font-bold">{tAdmin.admin.subscribers}</div>
                <div className="text-xs opacity-80">Manage users</div>
              </div>
            </Button>

            <Button
              variant="admin"
              className="w-full justify-start h-auto py-4"
              onClick={() => navigate("/admin/chat")}
            >
              <MessageCircle className="mr-3 w-5 h-5" />
              <div className="text-left">
                <div className="font-bold">{tAdmin.admin.chat}</div>
                <div className="text-xs opacity-80">View messages</div>
              </div>
            </Button>

            <Button
              variant="admin"
              className="w-full justify-start h-auto py-4"
              onClick={() => navigate("/admin/payments")}
            >
              <DollarSign className="mr-3 w-5 h-5" />
              <div className="text-left">
                <div className="font-bold">Payment Analytics</div>
                <div className="text-xs opacity-80">Track revenue & conversions</div>
              </div>
            </Button>
          </div>
        </MbCard>

        {/* Recent Activity */}
        <MbCard elevated className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Recent Activity</h2>
            {!loadingActivities && (
              <span className="text-xs text-muted-foreground">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>
          
          {loadingActivities ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-pulse text-muted-foreground">Loading activities...</div>
            </div>
          ) : activities.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No recent activity found
            </p>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => {
                const IconComponent = 
                  activity.icon === 'MessageCircle' ? MessageCircle :
                  activity.icon === 'Users' ? Users : Calendar;
                
                return (
                  <div 
                    key={activity.id} 
                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${activity.color}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {getRelativeTime(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </MbCard>
      </div>
      
      <AppFooter />
    </div>
  );
}
